/**
 * ScreeningCriteriaWizard — US-255b/c/d
 *
 * 6-step wizard for generating screening criteria with AI assistance.
 * Steps 1-4: data collection
 * Step 5: AI-generated facet card grid + plain-English summary + Full Document tab
 * Step 6: Save confirmation — shows replacement warning and calls PUT /criteria
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Sparkles,
  Loader2,
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  X,
  Plus,
  Undo2,
  Save,
} from 'lucide-react';
import { useGenerateCriteria, useUpdateJobCriteria } from '../../hooks/useApi';
import { useToast } from '../../contexts/ToastContext';

// ── Types ────────────────────────────────────────────────────────────────────

export interface WizardJob {
  id: number;
  title?: string | null;
  description?: string | null;
  existingCriteriaVersion?: number | null;
}

interface ClarifyQuestion {
  area: string;
  question: string;
}

interface Clarification {
  question: string;
  answer: string;
}

export interface ScreeningCriteriaWizardProps {
  isOpen: boolean;
  onClose: () => void;
  job: WizardJob;
}

// Canonical tier values from the API
export type CriterionTier = 'knockout' | 'critical' | 'high' | 'medium' | 'differentiator';

export interface CriterionCard {
  id: string;
  label: string;
  description: string;
  tier: CriterionTier;
}

// Display tier groups (three buttons shown in UI)
type DisplayTier = 'knockout' | 'required' | 'preferred';

function apiTierToDisplay(tier: CriterionTier): DisplayTier {
  if (tier === 'knockout') return 'knockout';
  if (tier === 'critical' || tier === 'high') return 'required';
  return 'preferred';
}

function displayTierToApi(display: DisplayTier): CriterionTier {
  if (display === 'knockout') return 'knockout';
  if (display === 'required') return 'critical';
  return 'medium';
}

// Reconstruct full # Screening Criteria text from current facet state
export function facetStateToText(cards: CriterionCard[]): string {
  const groups: Array<{ tier: CriterionTier; heading: string }> = [
    { tier: 'knockout', heading: 'Knockout Requirements' },
    { tier: 'critical', heading: 'Critical Requirements' },
    { tier: 'high', heading: 'High Requirements' },
    { tier: 'medium', heading: 'Preferred' },
    { tier: 'differentiator', heading: 'Differentiators' },
  ];

  const sections: string[] = ['# Screening Criteria'];
  for (const { tier, heading } of groups) {
    const items = cards.filter((c) => c.tier === tier);
    if (items.length === 0) continue;
    sections.push(`\n## ${heading}`);
    for (const item of items) {
      sections.push(`- ${item.label}: ${item.description}`);
    }
  }
  return sections.join('\n');
}

const TOTAL_STEPS = 6;

// ── Component ─────────────────────────────────────────────────────────────────

export function ScreeningCriteriaWizard({
  isOpen,
  onClose,
  job,
}: ScreeningCriteriaWizardProps) {
  const [step, setStep] = useState(1);

  // Step 1
  const [jobDescription, setJobDescription] = useState(job.description ?? '');
  const [showEmptyWarning, setShowEmptyWarning] = useState(false);

  // Step 2
  const [recruiterContext, setRecruiterContext] = useState('');

  // Step 3
  const [questions, setQuestions] = useState<ClarifyQuestion[]>([]);
  const [questionIdx, setQuestionIdx] = useState(0);
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [clarifications, setClarifications] = useState<Clarification[]>([]);
  const [clarifyError, setClarifyError] = useState<string | null>(null);
  const [clarifyLoaded, setClarifyLoaded] = useState(false);

  // Step 4
  const [forceInclude, setForceInclude] = useState('');

  // Step 5 — generate state
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  // Step 5 — facet state
  const [criteriaCards, setCriteriaCards] = useState<CriterionCard[]>([]);
  const [rawCriteriaText, setRawCriteriaText] = useState('');
  const [step5Tab, setStep5Tab] = useState<'facets' | 'document'>('facets');
  const [docEditMode, setDocEditMode] = useState(false);
  const [manualCriteriaText, setManualCriteriaText] = useState('');
  const [undoQueue, setUndoQueue] = useState<CriterionCard[]>([]);
  const [showAddInput, setShowAddInput] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const undoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track whether step 5 generation has been done — prevents re-triggering on back navigation
  const criteriaGeneratedRef = useRef(false);

  // Step 6 — save state
  const [pendingCriteriaText, setPendingCriteriaText] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);

  const generateMutation = useGenerateCriteria();
  const updateCriteriaMutation = useUpdateJobCriteria();
  const { showToast } = useToast();

  // Reset wizard state when it opens
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setJobDescription(job.description ?? '');
      setShowEmptyWarning(false);
      setRecruiterContext('');
      setQuestions([]);
      setQuestionIdx(0);
      setCurrentAnswer('');
      setClarifications([]);
      setClarifyError(null);
      setClarifyLoaded(false);
      setForceInclude('');
      setGenerateError(null);
      setIsGenerating(false);
      setCriteriaCards([]);
      setRawCriteriaText('');
      setStep5Tab('facets');
      setDocEditMode(false);
      setManualCriteriaText('');
      setUndoQueue([]);
      setShowAddInput(false);
      setNewLabel('');
      setNewDescription('');
      setPendingCriteriaText('');
      setSaveError(null);
      criteriaGeneratedRef.current = false;
    }
  }, [isOpen, job.description]);

  // When entering step 3, call the API
  const loadClarifyQuestions = useCallback(async () => {
    setClarifyError(null);
    setClarifyLoaded(false);
    setQuestions([]);
    setQuestionIdx(0);
    setCurrentAnswer('');
    setClarifications([]);
    try {
      const result = await generateMutation.mutateAsync({
        jobId: job.id,
        body: {
          jobDescription,
          recruiterContext,
          step: 'clarify',
        },
      });
      setQuestions(result.questions ?? []);
    } catch (err) {
      const error = err as Error;
      setClarifyError(error.message || 'Failed to load questions');
    } finally {
      setClarifyLoaded(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.id, jobDescription, recruiterContext]);

  useEffect(() => {
    if (step === 3) {
      loadClarifyQuestions();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // When entering step 5, call the generate API
  const loadGenerate = useCallback(async () => {
    setGenerateError(null);
    setIsGenerating(true);
    setCriteriaCards([]);
    setRawCriteriaText('');
    try {
      const result = await generateMutation.mutateAsync({
        jobId: job.id,
        body: {
          jobDescription,
          recruiterContext,
          clarifications,
          forceInclude,
          step: 'generate',
        },
      });
      const cards: CriterionCard[] = (result.criteriaList ?? []).map((c, idx) => ({
        id: `c-${idx}-${c.label.replace(/\s+/g, '-').toLowerCase()}`,
        label: c.label,
        description: c.description,
        tier: (c.tier as CriterionTier) ?? 'medium',
      }));
      setCriteriaCards(cards);
      setRawCriteriaText(result.criteriaText ?? '');
      setManualCriteriaText(result.criteriaText ?? '');
      criteriaGeneratedRef.current = true;
    } catch (err) {
      const error = err as Error;
      setGenerateError(error.message || 'Failed to generate criteria');
    } finally {
      setIsGenerating(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.id, jobDescription, recruiterContext, clarifications, forceInclude]);

  useEffect(() => {
    // Only generate when first entering step 5 (not when navigating back from step 6)
    if (step === 5 && !criteriaGeneratedRef.current) {
      loadGenerate();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // ── Facet helpers ──────────────────────────────────────────────────────────

  const setCardTier = (cardId: string, displayTier: DisplayTier) => {
    const card = criteriaCards.find((c) => c.id === cardId);
    if (!card) return;

    const currentDisplayTier = apiTierToDisplay(card.tier);
    if (currentDisplayTier === displayTier) {
      // Clicking the active button removes the card
      setCriteriaCards((prev) => prev.filter((c) => c.id !== cardId));
      setUndoQueue((prev) => [...prev, card]);
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
      undoTimerRef.current = setTimeout(() => {
        setUndoQueue([]);
      }, 5000);
    } else {
      setCriteriaCards((prev) =>
        prev.map((c) =>
          c.id === cardId ? { ...c, tier: displayTierToApi(displayTier) } : c
        )
      );
    }
  };

  const undoRemove = (card: CriterionCard) => {
    setCriteriaCards((prev) => [...prev, card]);
    setUndoQueue((prev) => prev.filter((c) => c.id !== card.id));
  };

  const addCriterion = () => {
    if (!newLabel.trim()) return;
    const id = `c-new-${Date.now()}`;
    setCriteriaCards((prev) => [
      ...prev,
      { id, label: newLabel.trim(), description: newDescription.trim(), tier: 'medium' },
    ]);
    setNewLabel('');
    setNewDescription('');
    setShowAddInput(false);
  };

  if (!isOpen) return null;

  // ── Navigation helpers ──────────────────────────────────────────────────────

  const goBack = () => setStep((s) => Math.max(1, s - 1));

  const handleStep1Next = () => {
    if (!jobDescription.trim() && !showEmptyWarning) {
      // First click with empty description: show warning but stay on step 1
      setShowEmptyWarning(true);
      return;
    }
    // Either description is filled, or user clicked Next again after seeing the warning
    setStep(2);
  };

  const handleStep2Next = () => setStep(3);

  const handleAnswerCurrent = (answer: string) => {
    const question = questions[questionIdx];
    if (!question) return;

    const newClarification: Clarification = {
      question: question.question,
      answer,
    };
    const updated = [...clarifications, newClarification];
    setClarifications(updated);

    if (questionIdx < questions.length - 1) {
      setQuestionIdx((i) => i + 1);
      setCurrentAnswer('');
    } else {
      // All questions answered — advance to step 4
      setStep(4);
    }
  };

  const handleSkipQuestion = () => handleAnswerCurrent('');

  const handleStep4Next = () => setStep(5);

  const handleStep3Back = () => {
    setStep(2);
    // Answers cleared when re-entering step 3 (loadClarifyQuestions resets everything)
  };

  const handleCancel = () => {
    onClose();
  };

  const handleSaveCriteria = async () => {
    setSaveError(null);
    try {
      await updateCriteriaMutation.mutateAsync({
        jobId: job.id,
        criteriaText: pendingCriteriaText,
        changeReason: 'AI generated via wizard',
        source: 'ai_generated',
      });
      showToast('Screening criteria updated', 'success');
      onClose();
    } catch (err) {
      const error = err as Error;
      setSaveError(error.message || 'Failed to save criteria');
    }
  };

  // ── Render steps ────────────────────────────────────────────────────────────

  const renderStepIndicator = () => (
    <div className="flex items-center justify-between px-6 pt-5 pb-1">
      <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
        Step {step} of {TOTAL_STEPS}
      </span>
      <div className="flex gap-1">
        {Array.from({ length: TOTAL_STEPS }, (_, i) => (
          <div
            key={i}
            className={`h-1.5 w-6 rounded-full transition-colors ${
              i + 1 <= step
                ? 'bg-primary-500 dark:bg-primary-400'
                : 'bg-gray-200 dark:bg-gray-700'
            }`}
          />
        ))}
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Job Description
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Arbie will use this to generate targeted screening criteria. Pre-filled from the job record.
        </p>
      </div>

      {showEmptyWarning && !jobDescription.trim() && (
        <div
          className="flex items-start gap-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 p-3"
          data-testid="empty-description-warning"
        >
          <AlertTriangle className="h-4 w-4 text-amber-500 dark:text-amber-400 mt-0.5 shrink-0" />
          <p className="text-sm text-amber-700 dark:text-amber-300">
            Output will be less precise without a job description
          </p>
        </div>
      )}

      <textarea
        value={jobDescription}
        onChange={(e) => setJobDescription(e.target.value)}
        placeholder="Paste the job description here..."
        rows={8}
        className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 resize-y"
        data-testid="job-description-input"
      />
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Recruiter Context
        </h3>
        <label className="mt-1 block text-sm text-gray-600 dark:text-gray-400">
          Anything you already know must be true for a candidate to qualify?
        </label>
      </div>

      <textarea
        value={recruiterContext}
        onChange={(e) => setRecruiterContext(e.target.value)}
        placeholder="e.g. Must be onsite in Salt Lake City, Python required, No software QA backgrounds"
        rows={5}
        className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 resize-y"
        data-testid="recruiter-context-input"
      />
      <p className="text-xs text-gray-400 dark:text-gray-500">Optional — leave blank to skip</p>
    </div>
  );

  const renderStep3 = () => {
    // Show spinner while the load hasn't completed yet (clarifyLoaded gates auto-advance)
    const isLoading = !clarifyLoaded;
    const currentQuestion = questions[questionIdx];

    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center py-12 space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary-500 dark:text-primary-400" />
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Arbie is reviewing the job description...
          </p>
        </div>
      );
    }

    if (clarifyError) {
      return (
        <div className="space-y-4">
          <div className="flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 p-3">
            <AlertTriangle className="h-4 w-4 text-red-500 dark:text-red-400 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-red-700 dark:text-red-300">Could not load questions</p>
              <p className="text-sm text-red-600 dark:text-red-400">{clarifyError}</p>
            </div>
          </div>
          <button onClick={loadClarifyQuestions} className="btn btn-secondary text-sm">
            Try again
          </button>
        </div>
      );
    }

    // AI returned no clarifying questions — nothing to ask, skip straight to step 4
    if (questions.length === 0) {
      setStep(4);
      return null;
    }

    return (
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Clarifying Questions
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Question {questionIdx + 1} of {questions.length}
          </p>
        </div>

        {/* Progress dots */}
        <div className="flex gap-1.5">
          {questions.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 w-6 rounded-full transition-colors ${
                i < questionIdx
                  ? 'bg-green-500 dark:bg-green-400'
                  : i === questionIdx
                  ? 'bg-primary-500 dark:bg-primary-400'
                  : 'bg-gray-200 dark:bg-gray-700'
              }`}
            />
          ))}
        </div>

        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-4">
          <p
            className="text-sm font-medium text-gray-900 dark:text-gray-100"
            data-testid="clarify-question-text"
          >
            {currentQuestion?.question}
          </p>
        </div>

        <textarea
          value={currentAnswer}
          onChange={(e) => setCurrentAnswer(e.target.value)}
          placeholder="Your answer (or click Skip if not applicable)"
          rows={4}
          className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 resize-y"
          data-testid="clarify-answer-input"
        />

        <div className="flex items-center gap-3">
          <button
            onClick={() => handleAnswerCurrent(currentAnswer)}
            className="btn btn-primary text-sm"
            data-testid="clarify-next-btn"
          >
            {questionIdx < questions.length - 1 ? 'Next' : 'Done'}
          </button>
          <button
            onClick={handleSkipQuestion}
            className="btn btn-secondary text-sm"
            data-testid="clarify-skip-btn"
          >
            Skip / Not applicable
          </button>
        </div>
      </div>
    );
  };

  const renderStep4 = () => (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Force-Include
        </h3>
        <label className="mt-1 block text-sm text-gray-600 dark:text-gray-400">
          Anything you want to be absolutely sure makes it in?
        </label>
        <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
          Add any must-haves, dealbreakers, or specific requirements Arbie might have missed.
        </p>
      </div>

      <textarea
        value={forceInclude}
        onChange={(e) => setForceInclude(e.target.value)}
        placeholder={
          'Candidate must be located in the United States\nMust have a degree in Accounting\nNo candidates from staffing or consulting backgrounds'
        }
        rows={6}
        className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 resize-y"
        data-testid="force-include-input"
      />
      <p className="text-xs text-gray-400 dark:text-gray-500">Optional — leave blank to skip</p>
    </div>
  );

  // ── Plain-English summary ───────────────────────────────────────────────────

  const renderSummary = () => {
    const knockouts = criteriaCards.filter((c) => c.tier === 'knockout');
    const required = criteriaCards.filter((c) => c.tier === 'critical' || c.tier === 'high');
    const preferred = criteriaCards.filter(
      (c) => c.tier === 'medium' || c.tier === 'differentiator'
    );
    const hasAny = knockouts.length + required.length + preferred.length > 0;
    if (!hasAny) {
      return (
        <p className="text-xs text-gray-400 dark:text-gray-500 italic">
          No criteria yet
        </p>
      );
    }
    return (
      <div className="space-y-3 text-sm" data-testid="summary-panel">
        {knockouts.length > 0 && (
          <div>
            <p className="font-semibold text-red-600 dark:text-red-400 text-xs uppercase tracking-wide mb-1">
              Knockouts — missing any = Not Qualified
            </p>
            <ul className="space-y-0.5">
              {knockouts.map((c) => (
                <li key={c.id} className="text-gray-700 dark:text-gray-300 text-xs">
                  • {c.label}
                </li>
              ))}
            </ul>
          </div>
        )}
        {required.length > 0 && (
          <div>
            <p className="font-semibold text-amber-600 dark:text-amber-400 text-xs uppercase tracking-wide mb-1">
              Required
            </p>
            <ul className="space-y-0.5">
              {required.map((c) => (
                <li key={c.id} className="text-gray-700 dark:text-gray-300 text-xs">
                  • {c.label}
                </li>
              ))}
            </ul>
          </div>
        )}
        {preferred.length > 0 && (
          <div>
            <p className="font-semibold text-green-600 dark:text-green-400 text-xs uppercase tracking-wide mb-1">
              Preferred
            </p>
            <ul className="space-y-0.5">
              {preferred.map((c) => (
                <li key={c.id} className="text-gray-700 dark:text-gray-300 text-xs">
                  • {c.label}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  // ── Facet card ──────────────────────────────────────────────────────────────

  const renderFacetCard = (card: CriterionCard) => {
    const displayTier = apiTierToDisplay(card.tier);
    return (
      <div
        key={card.id}
        className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 space-y-2"
        data-testid={`facet-card-${card.id}`}
      >
        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{card.label}</p>
        {card.description && (
          <p className="text-xs text-gray-500 dark:text-gray-400">{card.description}</p>
        )}
        <div className="flex gap-1">
          <button
            onClick={() => setCardTier(card.id, 'knockout')}
            data-testid={`tier-btn-knockout-${card.id}`}
            className={`rounded px-2 py-0.5 text-xs font-medium transition-colors ${
              displayTier === 'knockout'
                ? 'bg-red-500 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-red-100 dark:hover:bg-red-900/30'
            }`}
          >
            Knockout
          </button>
          <button
            onClick={() => setCardTier(card.id, 'required')}
            data-testid={`tier-btn-required-${card.id}`}
            className={`rounded px-2 py-0.5 text-xs font-medium transition-colors ${
              displayTier === 'required'
                ? 'bg-amber-500 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-amber-100 dark:hover:bg-amber-900/30'
            }`}
          >
            Required
          </button>
          <button
            onClick={() => setCardTier(card.id, 'preferred')}
            data-testid={`tier-btn-preferred-${card.id}`}
            className={`rounded px-2 py-0.5 text-xs font-medium transition-colors ${
              displayTier === 'preferred'
                ? 'bg-green-500 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-green-100 dark:hover:bg-green-900/30'
            }`}
          >
            Preferred
          </button>
        </div>
      </div>
    );
  };

  // ── Step 5 ──────────────────────────────────────────────────────────────────

  const renderStep5 = () => {
    if (isGenerating) {
      return (
        <div className="flex flex-col items-center justify-center py-12 space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary-500 dark:text-primary-400" />
          <p className="text-sm text-gray-600 dark:text-gray-400" data-testid="step5-loading">
            Arbie is generating your screening framework…
          </p>
        </div>
      );
    }

    if (generateError) {
      return (
        <div className="space-y-4">
          <div className="flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 p-3">
            <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-red-700 dark:text-red-300">
                Could not generate criteria
              </p>
              <p className="text-sm text-red-600 dark:text-red-400">{generateError}</p>
            </div>
          </div>
          <button
            onClick={() => {
              criteriaGeneratedRef.current = false;
              loadGenerate();
            }}
            className="btn btn-secondary text-sm"
            data-testid="step5-retry-btn"
          >
            Try again
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Review Screening Criteria
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Arbie generated {criteriaCards.length} criterion{criteriaCards.length !== 1 ? 'a' : ''}. Adjust tiers or add/remove criteria.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setStep5Tab('facets')}
            data-testid="tab-facets"
            className={`px-3 py-1.5 text-sm font-medium border-b-2 transition-colors ${
              step5Tab === 'facets'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            Facets
          </button>
          <button
            onClick={() => setStep5Tab('document')}
            data-testid="tab-document"
            className={`px-3 py-1.5 text-sm font-medium border-b-2 transition-colors ${
              step5Tab === 'document'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            Full Document
          </button>
        </div>

        {step5Tab === 'facets' ? (
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Facet grid */}
            <div className="flex-1 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {criteriaCards.map((card) => renderFacetCard(card))}
              </div>

              {/* Undo chips */}
              {undoQueue.length > 0 && (
                <div className="flex flex-wrap gap-2" data-testid="undo-chips">
                  {undoQueue.map((card) => (
                    <button
                      key={card.id}
                      onClick={() => undoRemove(card)}
                      data-testid={`undo-chip-${card.id}`}
                      className="inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-700 px-2.5 py-1 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                    >
                      <Undo2 className="h-3 w-3" />
                      Undo: {card.label}
                    </button>
                  ))}
                </div>
              )}

              {/* Add criterion */}
              {showAddInput ? (
                <div
                  className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 space-y-2"
                  data-testid="add-criterion-form"
                >
                  <input
                    value={newLabel}
                    onChange={(e) => setNewLabel(e.target.value)}
                    placeholder="Criterion label"
                    className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-2 py-1 text-sm"
                    data-testid="new-criterion-label"
                    autoFocus
                  />
                  <input
                    value={newDescription}
                    onChange={(e) => setNewDescription(e.target.value)}
                    placeholder="Description (optional)"
                    className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-2 py-1 text-sm"
                    data-testid="new-criterion-description"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={addCriterion}
                      className="btn btn-primary text-xs"
                      data-testid="add-criterion-confirm-btn"
                    >
                      Add
                    </button>
                    <button
                      onClick={() => {
                        setShowAddInput(false);
                        setNewLabel('');
                        setNewDescription('');
                      }}
                      className="btn btn-secondary text-xs"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowAddInput(true)}
                  className="flex items-center gap-1.5 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 px-3 py-2 text-sm text-gray-500 dark:text-gray-400 hover:border-gray-400 hover:text-gray-600 dark:hover:text-gray-300 w-full"
                  data-testid="add-criterion-btn"
                >
                  <Plus className="h-4 w-4" />
                  Add criterion
                </button>
              )}
            </div>

            {/* Summary panel */}
            <div className="lg:w-52 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-3">
              <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">
                Summary
              </p>
              {renderSummary()}
            </div>
          </div>
        ) : (
          /* Full Document tab */
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {docEditMode ? 'Editing — changes will be used when saving.' : 'Read-only view of the generated document.'}
              </p>
              <button
                onClick={() => setDocEditMode((m) => !m)}
                className="btn btn-secondary text-xs"
                data-testid="doc-edit-btn"
              >
                {docEditMode ? 'Done' : 'Edit'}
              </button>
            </div>
            {docEditMode ? (
              <textarea
                value={manualCriteriaText}
                onChange={(e) => setManualCriteriaText(e.target.value)}
                rows={16}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-xs font-mono text-gray-900 dark:text-gray-100 resize-y focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                data-testid="criteria-text-editor"
              />
            ) : (
              <pre
                className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-3 py-2 text-xs font-mono text-gray-800 dark:text-gray-200 overflow-auto max-h-80 whitespace-pre-wrap"
                data-testid="criteria-text-display"
              >
                {rawCriteriaText || '(no content)'}
              </pre>
            )}
          </div>
        )}
      </div>
    );
  };

  // ── Step 6 ──────────────────────────────────────────────────────────────────

  const renderStep6 = () => (
    <div className="space-y-4" data-testid="step6-content">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Save Screening Criteria
        </h3>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400" data-testid="step6-replacement-warning">
          This will replace the existing screening criteria
          {job.title ? ` for ${job.title}` : ''}.
        </p>
      </div>

      {job.existingCriteriaVersion != null && (
        <div
          className="flex items-start gap-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 p-3"
          data-testid="step6-version-warning"
        >
          <AlertTriangle className="h-4 w-4 text-amber-500 dark:text-amber-400 mt-0.5 shrink-0" />
          <p className="text-sm text-amber-700 dark:text-amber-300">
            Replacing existing criteria — version {job.existingCriteriaVersion}
          </p>
        </div>
      )}

      {saveError && (
        <div
          className="flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 p-3"
          data-testid="step6-save-error"
        >
          <AlertTriangle className="h-4 w-4 text-red-500 dark:text-red-400 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-red-700 dark:text-red-300">Failed to save</p>
            <p className="text-sm text-red-600 dark:text-red-400">{saveError}</p>
          </div>
        </div>
      )}

      {/* Preview of what will be saved */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-3">
        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
          Preview
        </p>
        <pre
          className="text-xs font-mono text-gray-700 dark:text-gray-300 whitespace-pre-wrap overflow-auto max-h-48"
          data-testid="step6-criteria-preview"
        >
          {pendingCriteriaText || '(no content)'}
        </pre>
      </div>
    </div>
  );

  // ── Footer buttons ──────────────────────────────────────────────────────────

  const renderFooter = () => {
    const backButton = step > 1 && (
      <button
        onClick={step === 3 ? handleStep3Back : goBack}
        className="btn btn-secondary text-sm"
        data-testid="wizard-back-btn"
      >
        <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
        Back
      </button>
    );

    const cancelButton = (
      <button
        onClick={handleCancel}
        className="btn btn-secondary text-sm"
        data-testid="wizard-cancel-btn"
      >
        Cancel
      </button>
    );

    if (step === 1) {
      return (
        <div className="flex items-center justify-between">
          {cancelButton}
          <button
            onClick={handleStep1Next}
            className="btn btn-primary text-sm"
            data-testid="wizard-next-btn"
          >
            Next
            <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
          </button>
        </div>
      );
    }

    if (step === 2) {
      return (
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            {backButton}
            {cancelButton}
          </div>
          <button
            onClick={handleStep2Next}
            className="btn btn-primary text-sm"
            data-testid="wizard-next-btn"
          >
            Next
            <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
          </button>
        </div>
      );
    }

    if (step === 3) {
      // Navigation handled inside step 3 content
      return (
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            {backButton}
            {cancelButton}
          </div>
        </div>
      );
    }

    if (step === 4) {
      return (
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            {backButton}
            {cancelButton}
          </div>
          <button
            onClick={handleStep4Next}
            className="btn btn-primary text-sm"
            data-testid="wizard-generate-btn"
          >
            <Sparkles className="mr-1.5 h-3.5 w-3.5" />
            Generate Criteria
          </button>
        </div>
      );
    }

    if (step === 5) {
      const isReady = !isGenerating && !generateError && criteriaCards.length > 0;
      const computeFinalText = () => {
        if (step5Tab === 'document' && docEditMode) return manualCriteriaText;
        return facetStateToText(criteriaCards);
      };
      return (
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            {backButton}
            {cancelButton}
          </div>
          {isReady && (
            <button
              onClick={() => {
                setPendingCriteriaText(computeFinalText());
                setSaveError(null);
                setStep(6);
              }}
              className="btn btn-primary text-sm"
              data-testid="wizard-save-criteria-btn"
            >
              Review & Save
              <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
            </button>
          )}
        </div>
      );
    }

    // Step 6 — save confirmation
    if (step === 6) {
      const isSaving = updateCriteriaMutation.isPending;
      return (
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            {backButton}
            {cancelButton}
          </div>
          <button
            onClick={handleSaveCriteria}
            disabled={isSaving}
            className="btn btn-primary text-sm"
            data-testid="wizard-confirm-save-btn"
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                Saving…
              </>
            ) : (
              <>
                <Save className="mr-1.5 h-3.5 w-3.5" />
                Save Criteria
              </>
            )}
          </button>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          {backButton}
          {cancelButton}
        </div>
      </div>
    );
  };

  // ── Main render ─────────────────────────────────────────────────────────────

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="wizard-title"
      data-testid="screening-criteria-wizard"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-gray-500/75 dark:bg-black/80 transition-opacity"
        aria-hidden="true"
        onClick={handleCancel}
      />

      {/* Modal panel */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className={`relative w-full bg-white dark:bg-gray-800 rounded-lg shadow-xl ${step === 5 ? 'max-w-3xl' : 'max-w-xl'}`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Step indicator + close */}
          <div className="flex items-center justify-between px-6 pt-5">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary-500 dark:text-primary-400" />
              <h2
                id="wizard-title"
                className="text-base font-semibold text-gray-900 dark:text-gray-100"
              >
                Generate Criteria
              </h2>
            </div>
            <button
              onClick={handleCancel}
              className="h-8 w-8 flex items-center justify-center rounded-md text-gray-400 dark:text-gray-500 hover:text-gray-500 hover:bg-gray-100 dark:hover:text-gray-400 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label="Close wizard"
              data-testid="wizard-close-btn"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {renderStepIndicator()}

          {/* Content */}
          <div className="px-6 py-4">
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
            {step === 4 && renderStep4()}
            {step === 5 && renderStep5()}
            {step === 6 && renderStep6()}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 rounded-b-lg">
            {renderFooter()}
          </div>
        </div>
      </div>
    </div>
  );
}
